 import { useState, useEffect } from "react";
import FAQnextgeni from "./FAQnextgenI";
import FAQnextgenj from "./FAQnextgenj";
import FAQskillsphere from "./FAQskillsphere";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function FQAHome() {
  const theme = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [faqData, setFaqData] = useState([]);
  const [faqLoading, setFaqLoading] = useState(true);
  const [faqError, setFaqError] = useState(null);

  const fetchFAQs = async () => {
    const API_URL = process.env.REACT_APP_API_URL;
    console.log("API URL:", API_URL);
    try {
      setFaqLoading(true);
      const response = await fetch(`${API_URL}/api/faq/getAllfaqs`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const filteredData = data.map(({ category, question, answer }) => ({
        category,
        question,
        answer
      }));
      setFaqData(filteredData);
      setFaqError(null);
    } catch (err) {
      setFaqError(err.message);
      setFaqData([]);
    } finally {
      setFaqLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  const tabs = [
    { name: "About the Company", category: "aoc", component: null },
    { name: "Nextgen Innovators", category: "innovator", component: FAQnextgeni },
    { name: "Nextgen Juniors", category: "juniors", component: FAQnextgenj },
    { name: "Skillsphere Assessment", category: "skillsphere", component: FAQskillsphere }
  ];

  const [activeTab, setActiveTab] = useState(0);

  const handleClick = (index) => {
    setActiveTab(index);
  };

  const getFilteredFAQs = () => {
    const activeCategory = tabs[activeTab].category;
    return faqData.filter(
      (faq) => faq.category.toLowerCase() === activeCategory
    );
  };

  const renderActiveComponent = () => {
    const filteredFAQs = getFilteredFAQs();
    const CurrentComponent = tabs[activeTab].component;

    if (CurrentComponent) {
      return (
        <CurrentComponent
          faqs={filteredFAQs}
          loading={faqLoading}
          error={faqError}
        />
      );
    }

    return (
      <div className="faq-container w-full px-2 sm:px-4">
        {faqLoading && <p className="text-center">Loading FAQs...</p>}
        {faqError && (
          <p className="text-red-500 text-center">Error: {faqError}</p>
        )}

        {!faqLoading && !faqError && filteredFAQs.length === 0 && (
          <p className="text-center text-gray-500">
            No FAQs found for this category
          </p>
        )}

        {filteredFAQs.map((faq, index) => (
          <Accordion 
            key={index} 
            sx={{ 
              mb: 2,
              '&.MuiAccordion-root': {
                minHeight: 50,
                backgroundColor: '#F8F9FA',
                borderRadius: '8px !important',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: 'scale(1)',
                '&:hover': {
                  backgroundColor: '#F1F3F5',
                  transform: 'scale(1.02)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                },
                '&.Mui-expanded': {
                  backgroundColor: 'white',
                  margin: '16px 0 !important',
                  transform: 'scale(1) !important',
                }
              }
            }}
          >
            <AccordionSummary
              expandIcon={
                <ArrowDownwardIcon sx={{ 
                  color: '#1976D2',
                  transition: 'transform 0.3s ease',
                  '&.Mui-expanded': {
                    transform: 'rotate(180deg)',
                    color: '#1976D2'
                  }
                }} />
              }
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
              sx={{
                '&.Mui-expanded': {
                  color: '#1976D2',
                  minHeight: 64,
                },
                '& .MuiAccordionSummary-content': {
                  margin: '16px 0',
                  transition: 'transform 0.2s ease',
                },
                minHeight: 64,
                padding: '0 16px',
                color: '#495057',
                '&:hover .MuiAccordionSummary-content': {
                  transform: 'translateX(4px)'
                }
              }}
            >
              <Typography 
                component="span" 
                fontWeight="semibold"
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: 18,
                  lineHeight: 1.5,
                  padding: '8px 0',
                  color: 'inherit',
                  transition: 'color 0.2s ease',
                  fontWeight:500
                }}
              >
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{
              padding: '16px 24px',
              backgroundColor: '#FFFFFF',
              borderRadius: '0 0 8px 8px',
              borderTop: '1px solid #E9ECEF',
              transition: 'all 0.3s ease-out',
              animation: 'fadeIn 0.3s ease-out',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(-10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}>
              <Typography sx={{ 
                fontFamily: "Poppins",
                lineHeight: 1.6,
                color: '#495057'
              }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full bg-white h-auto flex flex-col items-center relative overflow-hidden">
      {/* Global styles */}
      <style jsx global>{`
        @keyframes itemRise {
          0% { transform: translateY(0); }
          100% { transform: translateY(-5px); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
        }
        @keyframes borderGlow {
          0%, 100% { box-shadow: 0 0 5px rgba(53, 114, 255, 0.5); }
          50% { box-shadow: 0 0 15px rgba(53, 114, 255, 0.8); }
        }
        @keyframes iconPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        .floating-dot {
          position: absolute;
          border-radius: 50%;
          animation: float ease-in-out infinite;
          z-index: 0;
        }
        .dots-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          overflow: hidden;
          z-index: 0;
        }
      `}</style>

      {/* Floating dots background */}
      <div className="dots-container">
        {[5, 15, 25, 35, 45, 55, 65, 75, 85, 95, 10, 20, 30, 40, 50, 60, 70, 80, 90].map((top, i) => (
          <div 
            key={i}
            className="floating-dot"
            style={{
              top: `${top}%`,
              left: `${Math.random() * 100}%`,
              width: `${6 + (i % 5)}px`,
              height: `${6 + (i % 5)}px`,
              backgroundColor: ['#3572FF', '#06AED4', '#EC7D4E', '#9F7AEA', '#7F56D9', '#F59E0B'][i % 6],
              animationDelay: `${i * 0.2}s`,
              animationDuration: `${4 + (i % 6)}s`,
              opacity: 0.7
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
        <motion.div 
          whileHover={{scale: isXs ? 1.05 : 1.2}} 
          className={`${isXs ? 'text-2xl' : isSm ? 'text-3xl' : 'text-4xl'} lg:text-5xl text-[#1852FF] font-extrabold my-4 sm:my-6 text-center`}style={{ fontFamily: "Montserrat" }}
        >
          Frequently Asked Questions 
        </motion.div>

        <div className={`w-full ${isLg ? 'max-w-5xl' : 'max-w-6xl'} my-4 sm:my-8 mx-auto`}>
          <div className="flex flex-wrap sm:flex-nowrap justify-center sm:justify-between border-b-[1.5px] border-gray-300 overflow-x-hidden gap-2 sm:gap-4" style={{fontFamily: "Montserrat"}}>
            {tabs.map((tab, index) => (
              <button
                key={index}
                className={`px-4 py-2 sm:px-6 sm:py-3 text-base font-semibold sm:text-lg md:text-xl  transition-colors duration-300 whitespace-nowrap ${
                  activeTab === index
                    ? "text-blue-600 border-b-2 sm:border-b-4 border-blue-600"
                    : "text-gray-700 hover:text-blue-500"
                }`}
                onClick={() => handleClick(index)}  style={{ fontFamily: "Montserrat" }}
              > 
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        <div className={`w-full ${isLg ? 'max-w-5xl' : 'max-w-6xl'} p-2 sm:p-4 mt-2 sm:mt-4 mx-auto relative z-10`}>
          {renderActiveComponent()}
        </div>
      </div>
    </div>
  );
}